import { saveAlertConfig, saveData } from "../core/cloudSync.js";
import { genId } from "../core/utils.js";
import { navigateTo, render } from "../ui/app.js";

import { ALERTA_GRUPO_LABELS, BRAND, C, DOC_INFO, EMPRESA } from "../core/config.js";
import { state } from "../core/state.js";
import { addDias, btnStyle, escapeHTML, fmtCOP, fmtDate, inputStyle, today } from "../core/utils.js";
import { getDescuentosFinancieros, getSaldoOCP } from "./documentos.js";
import { calcularDiasRestantes, getCotizacionesPorEstadoVigencia } from "./reportes.js";

// Próximo plazo para renovar la matrícula mercantil. La renovación va del 1 de
// enero al 31 de marzo de cada año, así que del certificado de Cámara solo se
// necesita el último año renovado: el siguiente plazo es el 31 de marzo del año
// siguiente. Devuelve null si no se sabe (sin dato no se inventa una alerta).
export function getVencimientoMatricula() {
  const anio = parseInt(EMPRESA.matricula_anio_renovado, 10);
  if (!anio || anio < 2000 || anio > 2999) return null;
  const fecha = `${anio + 1}-03-31`;
  return { anio: anio + 1, fecha, dias: calcularDiasRestantes(fecha) };
}

export function getAlertas() {
  const cfg = state.alertConfig || { diasEntregaOP: 5, diasSeguimientoCT: 3, diasVencimientoCxP: 3, diasVencimientoResDian: 30 };

  // 1) OP: fechas de entrega próximas o vencidas
  const opsEntrega = [];
  (state.data.pedidos||[]).forEach(op => {
    if (op.estado === "cancelada") return;
    (op._entregas||[]).forEach(ent => {
      if (!ent.fecha) return;
      const dias = calcularDiasRestantes(ent.fecha);
      if (dias != null && dias <= cfg.diasEntregaOP) {
        const cliente = state.data.clientes.find(c => c.id === op.cliente_id) || {};
        opsEntrega.push({ op, cliente, fecha: ent.fecha, nota: ent.nota || "", dias });
      }
    });
  });
  opsEntrega.sort((a,b) => a.dias - b.dias);

  // 2) CT: vigencia por vencer o vencida
  const ctVig = getCotizacionesPorEstadoVigencia();
  const ctVigencia = [...ctVig.porVencer, ...ctVig.vencida].map(ct => ({
    ct, cliente: state.data.clientes.find(c => c.id === ct.cliente_id) || {}
  }));

  // 3) CT: seguimiento tras X días de enviada
  const ctSeguimiento = [];
  (state.data.cotizaciones||[]).forEach(ct => {
    if (ct.estado !== "enviada") return;
    const fechaRef = ct._fecha_enviada || ct.fecha;
    const dias = calcularDiasRestantes(fechaRef);
    if (dias == null) return;
    const diasTranscurridos = -dias;
    if (diasTranscurridos >= cfg.diasSeguimientoCT) {
      const cliente = state.data.clientes.find(c => c.id === ct.cliente_id) || {};
      ctSeguimiento.push({ ct, cliente, diasTranscurridos });
    }
  });
  ctSeguimiento.sort((a,b) => b.diasTranscurridos - a.diasTranscurridos);

  // 4) CxP: descuento financiero por vencer (X días antes del vencimiento)
  const cxpDescuento = [];
  (state.data.ocp||[]).forEach(ocp => {
    if (ocp.estado === "cancelada" || ocp.estado_pago === "pagada") return;
    const prov = state.data.proveedores.find(p => p.id === ocp.proveedor_id) || {};
    const condiciones = getDescuentosFinancieros(prov);
    if (condiciones.length === 0) return;
    const saldo = getSaldoOCP(ocp);
    condiciones.forEach(cond => {
      const vencimiento = addDias(ocp.fecha, cond.dias);
      if (!vencimiento) return;
      const dias = calcularDiasRestantes(vencimiento);
      if (dias != null && dias <= cfg.diasVencimientoCxP) {
        cxpDescuento.push({ ocp, prov, vencimiento, dias, descuento:cond, saldo });
      }
    });
  });
  cxpDescuento.sort((a,b) => a.dias - b.dias);

  // 5) Resolución DIAN de facturación: vigencia por vencer o vencida
  const resDian = [];
  (state.data.resoluciones_dian||[]).forEach(res => {
    if (res.activa === false) return;
    if (!res.fecha_hasta) return;
    const dias = calcularDiasRestantes(res.fecha_hasta);
    if (dias != null && dias <= cfg.diasVencimientoResDian) {
      resDian.push({ res, dias });
    }
  });
  resDian.sort((a,b) => a.dias - b.dias);

  // 6) Cámara de Comercio: renovación de la matrícula mercantil
  const matricula = [];
  const venc = getVencimientoMatricula();
  if (venc && venc.dias != null && venc.dias <= (cfg.diasVencimientoMatricula ?? 45)) {
    matricula.push({ ...venc, numero: EMPRESA.matricula_mercantil || "" });
  }

  // Excluir alertas que ya fueron procesadas y enviadas por WhatsApp
  const sentKeys = new Set((state.data.alertas_enviadas||[]).map(x => x.key));
  const opsEntregaActivas = opsEntrega.filter(a => !sentKeys.has(getAlertaKey("OP", a)));
  const ctVigenciaActivas = ctVigencia.filter(a => !sentKeys.has(getAlertaKey("CTVIG", a)));
  const ctSeguimientoActivas = ctSeguimiento.filter(a => !sentKeys.has(getAlertaKey("CTSEG", a)));
  const cxpDescuentoActivas = cxpDescuento.filter(a => !sentKeys.has(getAlertaKey("OCP", a)));
  const resDianActivas = resDian.filter(a => !sentKeys.has(getAlertaKey("RESDIAN", a)));
  const matriculaActivas = matricula.filter(a => !sentKeys.has(getAlertaKey("MATRICULA", a)));

  const total = opsEntregaActivas.length + ctVigenciaActivas.length + ctSeguimientoActivas.length
    + cxpDescuentoActivas.length + resDianActivas.length + matriculaActivas.length;
  return { opsEntrega: opsEntregaActivas, ctVigencia: ctVigenciaActivas, ctSeguimiento: ctSeguimientoActivas,
    cxpDescuento: cxpDescuentoActivas, resDian: resDianActivas, matricula: matriculaActivas, total };
}

export function buildWhatsAppLink(mensaje, telefono) {
  const tel = (telefono || state.alertConfig.whatsapp || "").replace(/[^0-9]/g, "");
  const base = tel ? `https://wa.me/${tel}` : `https://wa.me/`;
  return `${base}?text=${encodeURIComponent(mensaje)}`;
}

export function getAlertasNumerosEnvio() {
  const nums = state.alertConfig.whatsappNumbers || [];
  const ids = state.alertConfig.numerosEnvioIds || [];
  let seleccionados = nums.filter(n => ids.includes(n.id));
  if (seleccionados.length === 0 && nums.length > 0) seleccionados = [nums[0]];
  return seleccionados.map(n => (n.numero || "").replace(/[^0-9]/g, ""));
}

export function buildAlertasWhatsAppLink(mensaje, numero) {
  return `https://wa.me/57${numero}?text=${encodeURIComponent(mensaje)}`;
}

export function getAlertaKey(grupo, item) {
  switch (grupo) {
    case "OP":    return `OP-${item.op.id}-${item.fecha}`;
    case "CTVIG": return `CTVIG-${item.ct.id}`;
    case "CTSEG": return `CTSEG-${item.ct.id}`;
    case "OCP":   return `OCP-${item.ocp.id}-${item.descuento?item.descuento.medio:"x"}-${item.descuento?item.descuento.dias:0}`;
    case "RESDIAN": return `RESDIAN-${item.res.id}`;
    // Una sola matrícula por empresa: la clave la identifica el plazo concreto,
    // así que al año siguiente vuelve a avisar aunque se haya marcado enviada.
    case "MATRICULA": return `MATRICULA-${item.fecha}`;
    default:      return "";
  }
}

export function getAlertasKeysByFiltro(al, filtro) {
  const keys = [];
  if (filtro === "todos" || filtro === "OP")    al.opsEntrega.forEach(a => keys.push(getAlertaKey("OP", a)));
  if (filtro === "todos" || filtro === "CTVIG") al.ctVigencia.forEach(a => keys.push(getAlertaKey("CTVIG", a)));
  if (filtro === "todos" || filtro === "CTSEG") al.ctSeguimiento.forEach(a => keys.push(getAlertaKey("CTSEG", a)));
  if (filtro === "todos" || filtro === "OCP")   al.cxpDescuento.forEach(a => keys.push(getAlertaKey("OCP", a)));
  if (filtro === "todos" || filtro === "RESDIAN") al.resDian.forEach(a => keys.push(getAlertaKey("RESDIAN", a)));
  if (filtro === "todos" || filtro === "MATRICULA") (al.matricula||[]).forEach(a => keys.push(getAlertaKey("MATRICULA", a)));
  return keys;
}

export function getAlertasEntradas(al) {
  const entradas = [];

  al.opsEntrega.forEach(a => {
    const estado = a.dias < 0 ? `vencida hace ${Math.abs(a.dias)} día${Math.abs(a.dias)!==1?"s":""}`
      : a.dias === 0 ? "entrega es hoy" : `faltan ${a.dias} día${a.dias!==1?"s":""}`;
    entradas.push({
      grupo: "OP", key: getAlertaKey("OP", a),
      docTipo: "OP", docId: a.op.id, docNumero: a.op.numero, tercero: a.cliente.nombre||"—",
      linea: `🔧 OP ${a.op.numero} · ${a.cliente.nombre||"—"} · Entrega: ${fmtDate(a.fecha)} (${estado})`
    });
  });

  al.ctVigencia.forEach(a => {
    const dias = a.ct.diasRestantes;
    const estado = dias < 0 ? `vencida hace ${Math.abs(dias)} día${Math.abs(dias)!==1?"s":""}` : `vence en ${dias} día${dias!==1?"s":""}`;
    entradas.push({
      grupo: "CTVIG", key: getAlertaKey("CTVIG", a),
      docTipo: "CT", docId: a.ct.id, docNumero: a.ct.numero, tercero: a.cliente.nombre||"—",
      linea: `📋 CT ${a.ct.numero} · ${a.cliente.nombre||"—"} · Vigencia: ${fmtDate(a.ct.fecha_vigencia)} (${estado})`
    });
  });

  al.ctSeguimiento.forEach(a => {
    entradas.push({
      grupo: "CTSEG", key: getAlertaKey("CTSEG", a),
      docTipo: "CT", docId: a.ct.id, docNumero: a.ct.numero, tercero: a.cliente.nombre||"—",
      linea: `📤 CT ${a.ct.numero} · ${a.cliente.nombre||"—"} · enviada hace ${a.diasTranscurridos} día${a.diasTranscurridos!==1?"s":""}, pendiente de seguimiento`
    });
  });

  al.cxpDescuento.forEach(a => {
    const estado = a.dias < 0 ? `venció hace ${Math.abs(a.dias)} día${Math.abs(a.dias)!==1?"s":""}` : a.dias === 0 ? "vence hoy" : `vence en ${a.dias} día${a.dias!==1?"s":""}`;
    entradas.push({
      grupo: "OCP", key: getAlertaKey("OCP", a),
      docTipo: "OCP", docId: a.ocp.id, docNumero: a.ocp.numero, tercero: a.prov.nombre||"—",
      linea: `💸 OCP ${a.ocp.numero} · ${a.prov.nombre||"—"} · ${(a.descuento||{}).pct||0}% vía ${(a.descuento||{}).label||(a.descuento||{}).medio||"—"} (${(a.descuento||{}).dias||0}d) · Saldo: ${fmtCOP(a.saldo)} · Vence: ${fmtDate(a.vencimiento)} (${estado})`
    });
  });

  al.resDian.forEach(a => {
    const estado = a.dias < 0 ? `vencida hace ${Math.abs(a.dias)} día${Math.abs(a.dias)!==1?"s":""}` : a.dias === 0 ? "vence hoy" : `vence en ${a.dias} día${a.dias!==1?"s":""}`;
    entradas.push({
      grupo: "RESDIAN", key: getAlertaKey("RESDIAN", a),
      docTipo: "RESDIAN", docId: a.res.id, docNumero: a.res.numero_resolucion, tercero: "—",
      linea: `🧾 Resolución ${a.res.numero_resolucion||"—"} (prefijo ${a.res.prefijo||"—"}) · Vigente hasta: ${fmtDate(a.res.fecha_hasta)} (${estado})`
    });
  });

  (al.matricula||[]).forEach(a => {
    const estado = a.dias < 0 ? `el plazo venció hace ${Math.abs(a.dias)} día${Math.abs(a.dias)!==1?"s":""}` : a.dias === 0 ? "vence hoy" : `vence en ${a.dias} día${a.dias!==1?"s":""}`;
    entradas.push({
      grupo: "MATRICULA", key: getAlertaKey("MATRICULA", a),
      docTipo: "MATRICULA", docId: "matricula", docNumero: a.numero || "—", tercero: "—",
      linea: `🏛️ Matrícula mercantil ${a.numero||"—"} · Renovar antes del ${fmtDate(a.fecha)} (${estado})`
    });
  });

  return entradas;
}

export function buildAlertasMensajeUnificado(al) {
  const sel = state.alertasSeleccionadas || {};
  const entradas = getAlertasEntradas(al).filter(e => sel[e.key]);
  if (entradas.length === 0) return "";

  const bloques = [];
  Object.entries(ALERTA_GRUPO_LABELS).forEach(([grupo, info]) => {
    const items = entradas.filter(e => e.grupo === grupo);
    if (items.length === 0) return;
    const titulo = info.titulo.replace(/\*/g, "");
    const lista = items.map((e,i) => `${i+1}. ${e.linea}`).join("\n");
    bloques.push(`*${titulo}*\n${lista}`);
  });

  return `🔔 *Alertas ${BRAND.producto}* — ${fmtDate(today())}\n\n` + bloques.join("\n\n");
}

export function renderAlertas() {
  const cfg = state.alertConfig || { diasEntregaOP: 5, diasSeguimientoCT: 3, diasVencimientoCxP: 3, diasVencimientoResDian: 30, whatsapp: "", whatsappNumbers: [], numeroEnvioId: null };
  const al = getAlertas();
  const filtro = state.alertasFiltroTipo || "todos";
  const sel = state.alertasSeleccionadas || {};
  const visibleKeys = getAlertasKeysByFiltro(al, filtro);
  const totalSeleccionadas = Object.keys(sel).filter(k => sel[k]).length;
  const todasVisiblesSeleccionadas = visibleKeys.length > 0 && visibleKeys.every(k => sel[k]);

  const header = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:9px;padding-bottom:10px;border-bottom:3px solid ${C.gold};">
      <div style="display:flex;gap:10px;align-items:center;">
        <img src="${window.LOGO}" alt="Aura" style="height:42px;object-fit:contain;"/>
        <div>
          <h2 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">🔔 Centro de Alertas</h2>
          <p style="margin:3px 0 0;color:${C.muted};font-size:11.5px;">${al.total} alerta${al.total!==1?"s":""} activa${al.total!==1?"s":""}</p>
        </div>
      </div>
    </div>
  `;

  const numerosWA = cfg.whatsappNumbers || [];
  const numerosEnvioIds = cfg.numerosEnvioIds || [];
  const numerosEnvioSel = getAlertasNumerosEnvio();

  const numerosChipsHTML = numerosWA.length === 0
    ? `<div style="font-size:10.5px;color:${C.muted};">Sin números configurados aún. Agrega al menos uno para poder enviar alertas.</div>`
    : numerosWA.map(n => `
      <span style="display:inline-flex;align-items:center;gap:6px;background:#f1f5f9;border:1px solid ${C.border};border-radius:20px;padding:4px 6px 4px 12px;font-size:11.5px;margin:2px 4px 2px 0;">
        ${escapeHTML(n.nombre||"Sin nombre")}: 57${escapeHTML(n.numero)}
        <button data-action="eliminar-whatsapp-numero" data-id="${n.id}" style="background:transparent;border:none;color:${C.red};cursor:pointer;font-size:14px;padding:0 4px;line-height:1;" title="Eliminar número">✕</button>
      </span>
    `).join("");

  const configHTML = `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:18px;">
      <div style="font-size:11.5px;color:${C.muted};font-weight:700;letter-spacing:0.5px;margin-bottom:10px;">⚙️ CONFIGURACIÓN DE ALERTAS</div>
      <div style="display:flex;gap:10px;flex-wrap:wrap;align-items:flex-end;">
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Avisar entrega de OP con (días)</label>
          <input type="number" min="0" value="${cfg.diasEntregaOP}" style="${inputStyle}width:90px;text-align:right;" onchange="state.alertConfig.diasEntregaOP=parseInt(this.value)||0;saveAlertConfig();render();"/>
        </div>
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Seguimiento a CT enviada tras (días)</label>
          <input type="number" min="0" value="${cfg.diasSeguimientoCT}" style="${inputStyle}width:90px;text-align:right;" onchange="state.alertConfig.diasSeguimientoCT=parseInt(this.value)||0;saveAlertConfig();render();"/>
        </div>
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Avisar descuento CxP con (días antes)</label>
          <input type="number" min="0" value="${cfg.diasVencimientoCxP}" style="${inputStyle}width:90px;text-align:right;" onchange="state.alertConfig.diasVencimientoCxP=parseInt(this.value)||0;saveAlertConfig();render();"/>
        </div>
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Avisar vencimiento resolución DIAN con (días antes)</label>
          <input type="number" min="0" value="${cfg.diasVencimientoResDian}" style="${inputStyle}width:90px;text-align:right;" onchange="state.alertConfig.diasVencimientoResDian=parseInt(this.value)||0;saveAlertConfig();render();"/>
        </div>
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Avisar renovación de matrícula con (días antes)</label>
          <input type="number" min="0" value="${cfg.diasVencimientoMatricula ?? 45}" style="${inputStyle}width:90px;text-align:right;" onchange="state.alertConfig.diasVencimientoMatricula=parseInt(this.value)||0;saveAlertConfig();render();"/>
        </div>
      </div>
      <div style="margin-top:14px;padding-top:12px;border-top:1px solid ${C.border};">
        <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:6px;">📱 NÚMEROS DE WHATSAPP PARA ENVÍO DE ALERTAS (sin código de país)</label>
        <div style="margin-bottom:8px;">${numerosChipsHTML}</div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;">
          <div>
            <input id="nuevo-whatsapp-nombre" type="text" placeholder="Nombre (opcional)" style="${inputStyle}width:150px;"/>
          </div>
          <div>
            <input id="nuevo-whatsapp-numero" type="text" inputmode="numeric" placeholder="Ej: 3001234567" style="${inputStyle}width:150px;"/>
          </div>
          <button data-action="agregar-whatsapp-numero" style="${btnStyle(null,"secondary")}">+ Agregar número</button>
        </div>
      </div>
      <div style="font-size:10px;color:${C.muted};margin-top:8px;">💡 El botón "Enviar alertas seleccionadas" abre WhatsApp Web/App con el mensaje redactado (uno por destinatario marcado en "Enviar a"); tú confirmas cada envío.</div>
    </div>
  `;

  const filtroOpciones = [
    { value:"todos", label:`Todos los tipos (${al.total})` },
    { value:"OP",    label:`🔧 Entregas de OP (${al.opsEntrega.length})` },
    { value:"CTVIG", label:`📋 Vigencia de CT (${al.ctVigencia.length})` },
    { value:"CTSEG", label:`📤 Seguimiento de CT (${al.ctSeguimiento.length})` },
    { value:"OCP",   label:`💸 Descuento CxP (${al.cxpDescuento.length})` },
    { value:"RESDIAN", label:`🧾 Resolución DIAN (${al.resDian.length})` },
    { value:"MATRICULA", label:`🏛️ Renovación de matrícula (${(al.matricula||[]).length})` }
  ];

  const enviarBtn = (totalSeleccionadas > 0 && numerosEnvioSel.length > 0)
    ? `<button data-action="enviar-alertas-seleccionadas" style="${btnStyle("#25D366")}">📲 Enviar ${totalSeleccionadas} alerta${totalSeleccionadas!==1?"s":""} a ${numerosEnvioSel.length} destinatario${numerosEnvioSel.length!==1?"s":""}</button>`
    : `<span style="${btnStyle(null,"secondary")}opacity:.5;cursor:not-allowed;" title="${numerosWA.length===0?"Configura al menos un número de WhatsApp arriba":(numerosEnvioSel.length===0?"Marca al menos un destinatario en \\'Enviar a\\'":"Selecciona al menos una alerta")}">📲 Enviar seleccionadas</span>`;

  const numeroEnvioCheckboxesHTML = numerosWA.length > 0 ? `
    <div>
      <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Enviar a</label>
      <div style="display:flex;gap:10px;flex-wrap:wrap;align-items:center;padding:8px 10px;border:1px solid ${C.border};border-radius:7px;background:white;">
        ${numerosWA.map(n => `
          <label style="display:flex;align-items:center;gap:5px;font-size:11.5px;cursor:pointer;color:${C.text};">
            <input type="checkbox" data-action="toggle-numero-envio" data-id="${n.id}" ${numerosEnvioIds.includes(n.id)?"checked":""} style="cursor:pointer;width:15px;height:15px;"/>
            ${escapeHTML(n.nombre||"Sin nombre")} (57${escapeHTML(n.numero)})
          </label>
        `).join("")}
      </div>
    </div>
  ` : "";

  const filtroBarHTML = `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:18px;display:flex;gap:9px;flex-wrap:wrap;align-items:flex-end;justify-content:space-between;">
      <div>
        <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Filtrar por tipo de documento</label>
        <select style="${inputStyle}min-width:220px;" onchange="state.alertasFiltroTipo=this.value;render();">
          ${filtroOpciones.map(o => `<option value="${o.value}" ${filtro===o.value?"selected":""}>${o.label}</option>`).join("")}
        </select>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;">
        <button data-action="select-all-alertas" style="${btnStyle(null,"secondary")}">${todasVisiblesSeleccionadas?"✓ ":""}Seleccionar todo</button>
        <button data-action="deselect-all-alertas" style="${btnStyle(null,"secondary")}">Desmarcar selección</button>
        ${numeroEnvioCheckboxesHTML}
        ${enviarBtn}
      </div>
    </div>
  `;

  // 1) OP entregas
  const opsHTML = (filtro !== "todos" && filtro !== "OP") ? "" : `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:18px;">
      <div style="font-size:11.5px;color:${C.muted};font-weight:700;letter-spacing:0.5px;margin-bottom:10px;">🔧 ÓRDENES DE PRODUCCIÓN — FECHAS DE ENTREGA (${al.opsEntrega.length})</div>
      ${al.opsEntrega.length === 0 ? `<div style="font-size:11.5px;color:${C.muted};">Sin entregas próximas o vencidas.</div>` : al.opsEntrega.map(a => {
        const key = getAlertaKey("OP", a);
        const estadoTxt = a.dias < 0 ? `<span style="color:#ef4444;font-weight:700;">Vencida hace ${Math.abs(a.dias)} día${Math.abs(a.dias)!==1?"s":""}</span>` : (a.dias === 0 ? `<span style="color:#f59e0b;font-weight:700;">Entrega es hoy</span>` : `<span style="color:#f59e0b;font-weight:700;">Faltan ${a.dias} día${a.dias!==1?"s":""}</span>`);
        const mensaje = `Hola, te recordamos que la entrega de la orden ${a.op.numero} (${escapeHTML(a.cliente.nombre||"")}) está programada para el ${fmtDate(a.fecha)}.${a.nota?" Nota: "+a.nota:""}`;
        return `
          <div style="display:flex;justify-content:space-between;align-items:center;padding:10px;border-top:1px solid ${C.border};gap:10px;flex-wrap:wrap;">
            <div style="display:flex;align-items:flex-start;gap:10px;">
              <input type="checkbox" data-action="toggle-alerta-check" data-key="${key}" ${sel[key]?"checked":""} style="cursor:pointer;width:18px;height:18px;margin-top:2px;"/>
              <div>
                <span data-action="ir-a-documento" data-tipo="OP" data-id="${a.op.id}" style="color:${DOC_INFO.OP.color};font-weight:700;cursor:pointer;text-decoration:underline;">${escapeHTML(a.op.numero)}</span>
                <span style="color:${C.muted};font-size:11.5px;"> · ${escapeHTML(a.cliente.nombre||"—")} · Entrega: ${fmtDate(a.fecha)}${a.nota?` · ${escapeHTML(a.nota)}`:""}</span>
                <div style="font-size:10.5px;margin-top:2px;">${estadoTxt}</div>
              </div>
            </div>
            <a href="${buildWhatsAppLink(mensaje, a.cliente.telefono)}" target="_blank" style="${btnStyle("#25D366",null,true)}text-decoration:none;">📲 WhatsApp</a>
          </div>
        `;
      }).join("")}
    </div>
  `;

  // 2) CT vigencia
  const ctVigHTML = (filtro !== "todos" && filtro !== "CTVIG") ? "" : `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:18px;">
      <div style="font-size:11.5px;color:${C.muted};font-weight:700;letter-spacing:0.5px;margin-bottom:10px;">📋 COTIZACIONES — VIGENCIA POR VENCER O VENCIDA (${al.ctVigencia.length})</div>
      ${al.ctVigencia.length === 0 ? `<div style="font-size:11.5px;color:${C.muted};">Sin cotizaciones por vencer o vencidas.</div>` : al.ctVigencia.map(a => {
        const key = getAlertaKey("CTVIG", a);
        const dias = a.ct.diasRestantes;
        const estadoTxt = dias < 0 ? `<span style="color:#ef4444;font-weight:700;">Vencida hace ${Math.abs(dias)} día${Math.abs(dias)!==1?"s":""}</span>` : `<span style="color:#f59e0b;font-weight:700;">Vence en ${dias} día${dias!==1?"s":""}</span>`;
        const mensaje = `Hola, la cotización ${a.ct.numero}${dias < 0 ? " venció el " : " vence el "}${fmtDate(a.ct.fecha_vigencia)}. Quedamos atentos para confirmar si deseas continuar con ella.`;
        return `
          <div style="display:flex;justify-content:space-between;align-items:center;padding:10px;border-top:1px solid ${C.border};gap:10px;flex-wrap:wrap;">
            <div style="display:flex;align-items:flex-start;gap:10px;">
              <input type="checkbox" data-action="toggle-alerta-check" data-key="${key}" ${sel[key]?"checked":""} style="cursor:pointer;width:18px;height:18px;margin-top:2px;"/>
              <div>
                <span data-action="ir-a-documento" data-tipo="CT" data-id="${a.ct.id}" style="color:${DOC_INFO.CT.color};font-weight:700;cursor:pointer;text-decoration:underline;">${escapeHTML(a.ct.numero)}</span>
                <span style="color:${C.muted};font-size:11.5px;"> · ${escapeHTML(a.cliente.nombre||"—")} · Vigencia: ${fmtDate(a.ct.fecha_vigencia)}</span>
                <div style="font-size:10.5px;margin-top:2px;">${estadoTxt}</div>
              </div>
            </div>
            <a href="${buildWhatsAppLink(mensaje, a.cliente.telefono)}" target="_blank" style="${btnStyle("#25D366",null,true)}text-decoration:none;">📲 WhatsApp</a>
          </div>
        `;
      }).join("")}
    </div>
  `;

  // 3) CT seguimiento
  const ctSegHTML = (filtro !== "todos" && filtro !== "CTSEG") ? "" : `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:18px;">
      <div style="font-size:11.5px;color:${C.muted};font-weight:700;letter-spacing:0.5px;margin-bottom:10px;">📤 COTIZACIONES — SEGUIMIENTO TRAS ENVÍO (${al.ctSeguimiento.length})</div>
      ${al.ctSeguimiento.length === 0 ? `<div style="font-size:11.5px;color:${C.muted};">Sin cotizaciones pendientes de seguimiento.</div>` : al.ctSeguimiento.map(a => {
        const key = getAlertaKey("CTSEG", a);
        const mensaje = `Hola, queremos hacer seguimiento a la cotización ${a.ct.numero} enviada hace ${a.diasTranscurridos} días. ¿Has tenido oportunidad de revisarla?`;
        return `
          <div style="display:flex;justify-content:space-between;align-items:center;padding:10px;border-top:1px solid ${C.border};gap:10px;flex-wrap:wrap;">
            <div style="display:flex;align-items:flex-start;gap:10px;">
              <input type="checkbox" data-action="toggle-alerta-check" data-key="${key}" ${sel[key]?"checked":""} style="cursor:pointer;width:18px;height:18px;margin-top:2px;"/>
              <div>
                <span data-action="ir-a-documento" data-tipo="CT" data-id="${a.ct.id}" style="color:${DOC_INFO.CT.color};font-weight:700;cursor:pointer;text-decoration:underline;">${escapeHTML(a.ct.numero)}</span>
                <span style="color:${C.muted};font-size:11.5px;"> · ${escapeHTML(a.cliente.nombre||"—")} · Enviada hace <strong style="color:#f59e0b;">${a.diasTranscurridos} día${a.diasTranscurridos!==1?"s":""}</strong></span>
              </div>
            </div>
            <a href="${buildWhatsAppLink(mensaje, a.cliente.telefono)}" target="_blank" style="${btnStyle("#25D366",null,true)}text-decoration:none;">📲 WhatsApp</a>
          </div>
        `;
      }).join("")}
    </div>
  `;

  // 4) CxP descuento financiero
  const cxpHTML = (filtro !== "todos" && filtro !== "OCP") ? "" : `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:18px;">
      <div style="font-size:11.5px;color:${C.muted};font-weight:700;letter-spacing:0.5px;margin-bottom:10px;">💸 CUENTAS POR PAGAR — DESCUENTO FINANCIERO POR VENCER (${al.cxpDescuento.length})</div>
      ${al.cxpDescuento.length === 0 ? `<div style="font-size:11.5px;color:${C.muted};">Sin descuentos financieros próximos a vencer.</div>` : al.cxpDescuento.map(a => {
        const key = getAlertaKey("OCP", a);
        const estadoTxt = a.dias < 0
          ? `<span style="color:#ef4444;font-weight:700;">⚠️ Venció hace ${Math.abs(a.dias)}d</span>`
          : a.dias === 0 ? `<span style="color:#f59e0b;font-weight:700;">⏰ Vence HOY</span>`
          : `<span style="color:#f59e0b;font-weight:700;">⏳ ${a.dias}d restantes</span>`;
        const cond = a.descuento || {};
        const diasLabel = (cond.dias||0)===0 ? "contado/anticipado" : `a ${cond.dias} días`;
        const descTxt = `${cond.label||cond.medio||"—"} — ${cond.pct||0}% (${diasLabel})`;
        const mensaje = `Recordatorio: OCP ${a.ocp.numero} (saldo ${fmtCOP(a.saldo)}) · Vence ${fmtDate(a.vencimiento)} · Descuento: ${cond.pct||0}% ${diasLabel} vía ${cond.label||cond.medio||"—"}.`;
        return `
          <div style="display:flex;justify-content:space-between;align-items:center;padding:10px;border-top:1px solid ${C.border};gap:10px;flex-wrap:wrap;">
            <div style="display:flex;align-items:flex-start;gap:10px;">
              <input type="checkbox" data-action="toggle-alerta-check" data-key="${key}" ${sel[key]?"checked":""} style="cursor:pointer;width:18px;height:18px;margin-top:2px;"/>
              <div>
                <span data-action="ir-a-documento" data-tipo="OCP" data-id="${a.ocp.id}" style="color:${DOC_INFO.OCP.color};font-weight:700;cursor:pointer;text-decoration:underline;">${escapeHTML(a.ocp.numero)}</span>
                <span style="color:${C.muted};font-size:11.5px;"> · ${escapeHTML(a.prov.nombre||"—")} · Saldo: ${fmtCOP(a.saldo)}</span>
                <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:4px;align-items:center;">
                  ${estadoTxt}
                  <span style="background:${C.accent}15;color:${C.accent};padding:2px 8px;border-radius:6px;font-size:10.5px;font-weight:600;">💸 ${escapeHTML(descTxt)}</span>
                  <span style="font-size:10.5px;color:${C.muted};">Límite: ${fmtDate(a.vencimiento)}</span>
                </div>
              </div>
            </div>
            <a href="${buildWhatsAppLink(mensaje, a.prov.telefono)}" target="_blank" style="${btnStyle("#25D366",null,true)}text-decoration:none;">📲 WhatsApp</a>
          </div>
        `;
      }).join("")}
    </div>
  `;

  // 5) Resolución DIAN — vigencia por vencer
  const resDianHTML = (filtro !== "todos" && filtro !== "RESDIAN") ? "" : `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:18px;">
      <div style="font-size:11.5px;color:${C.muted};font-weight:700;letter-spacing:0.5px;margin-bottom:10px;">🧾 RESOLUCIÓN DIAN — VIGENCIA POR VENCER O VENCIDA (${al.resDian.length})</div>
      ${al.resDian.length === 0 ? `<div style="font-size:11.5px;color:${C.muted};">Sin resoluciones por vencer o vencidas.</div>` : al.resDian.map(a => {
        const key = getAlertaKey("RESDIAN", a);
        const estadoTxt = a.dias < 0 ? `<span style="color:#ef4444;font-weight:700;">Vencida hace ${Math.abs(a.dias)} día${Math.abs(a.dias)!==1?"s":""}</span>` : (a.dias === 0 ? `<span style="color:#f59e0b;font-weight:700;">Vence hoy</span>` : `<span style="color:#f59e0b;font-weight:700;">Vence en ${a.dias} día${a.dias!==1?"s":""}</span>`);
        const mensaje = `⚠️ La resolución DIAN ${a.res.numero_resolucion||""} (prefijo ${a.res.prefijo||""}) ${a.dias < 0 ? "venció el " : "vence el "}${fmtDate(a.res.fecha_hasta)}. Es necesario tramitar la renovación con la DIAN.`;
        return `
          <div style="display:flex;justify-content:space-between;align-items:center;padding:10px;border-top:1px solid ${C.border};gap:10px;flex-wrap:wrap;">
            <div style="display:flex;align-items:flex-start;gap:10px;">
              <input type="checkbox" data-action="toggle-alerta-check" data-key="${key}" ${sel[key]?"checked":""} style="cursor:pointer;width:18px;height:18px;margin-top:2px;"/>
              <div>
                <span data-action="ir-parametros-fiscales" style="color:#dc2626;font-weight:700;cursor:pointer;text-decoration:underline;">Resolución ${escapeHTML(a.res.numero_resolucion||"—")}</span>
                <span style="color:${C.muted};font-size:11.5px;"> · Prefijo ${escapeHTML(a.res.prefijo||"—")} · Vigente hasta: ${fmtDate(a.res.fecha_hasta)}</span>
                <div style="font-size:10.5px;margin-top:2px;">${estadoTxt}</div>
              </div>
            </div>
            <a href="${buildWhatsAppLink(mensaje, "")}" target="_blank" style="${btnStyle("#25D366",null,true)}text-decoration:none;">📲 WhatsApp</a>
          </div>
        `;
      }).join("")}
    </div>
  `;

  // 6) Cámara de Comercio — renovación de la matrícula mercantil
  const matriculaHTML = (filtro !== "todos" && filtro !== "MATRICULA") ? "" : `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:18px;">
      <div style="font-size:11.5px;color:${C.muted};font-weight:700;letter-spacing:0.5px;margin-bottom:10px;">🏛️ CÁMARA DE COMERCIO — RENOVACIÓN DE MATRÍCULA (${(al.matricula||[]).length})</div>
      ${(al.matricula||[]).length === 0 ? `<div style="font-size:11.5px;color:${C.muted};">Sin renovaciones próximas o vencidas.</div>` : al.matricula.map(a => {
        const key = getAlertaKey("MATRICULA", a);
        const estadoTxt = a.dias < 0 ? `<span style="color:#ef4444;font-weight:700;">Plazo vencido hace ${Math.abs(a.dias)} día${Math.abs(a.dias)!==1?"s":""}</span>` : (a.dias === 0 ? `<span style="color:#f59e0b;font-weight:700;">Último día para renovar</span>` : `<span style="color:#f59e0b;font-weight:700;">Faltan ${a.dias} día${a.dias!==1?"s":""}</span>`);
        const mensaje = `⚠️ La matrícula mercantil ${a.numero||""} debe renovarse en la Cámara de Comercio antes del ${fmtDate(a.fecha)}${a.dias < 0 ? " — el plazo ya venció" : ""}.`;
        return `
          <div style="display:flex;justify-content:space-between;align-items:center;padding:10px;border-top:1px solid ${C.border};gap:10px;flex-wrap:wrap;">
            <div style="display:flex;align-items:flex-start;gap:10px;">
              <input type="checkbox" data-action="toggle-alerta-check" data-key="${key}" ${sel[key]?"checked":""} style="cursor:pointer;width:18px;height:18px;margin-top:2px;"/>
              <div>
                <span data-action="ir-configuracion-empresa" style="color:#b45309;font-weight:700;cursor:pointer;text-decoration:underline;">Matrícula ${escapeHTML(a.numero||"—")}</span>
                <span style="color:${C.muted};font-size:11.5px;"> · Renovar antes del ${fmtDate(a.fecha)}</span>
                <div style="font-size:10.5px;margin-top:2px;">${estadoTxt}</div>
              </div>
            </div>
            <a href="${buildWhatsAppLink(mensaje, "")}" target="_blank" style="${btnStyle("#25D366",null,true)}text-decoration:none;">📲 WhatsApp</a>
          </div>
        `;
      }).join("")}
    </div>
  `;

  // 7) Alertas procesadas y enviadas (agrupadas por tipo de documento, con filtro propio)
  const enviadas = state.data.alertas_enviadas || [];
  const enviadasPorGrupo = {};
  enviadas.forEach(e => { (enviadasPorGrupo[e.grupo] = enviadasPorGrupo[e.grupo] || []).push(e); });
  const enviadasOrdenadas = (arr) => [...arr].sort((a,b) => (b.fecha||"").localeCompare(a.fecha||""));

  const filtroEnv = state.alertasEnviadasFiltroTipo || "todos";
  const filtroOpcionesEnviadas = [
    { value:"todos", label:`Todos los tipos (${enviadas.length})` },
    { value:"OP",    label:`🔧 Entregas de OP (${(enviadasPorGrupo.OP||[]).length})` },
    { value:"CTVIG", label:`📋 Vigencia de CT (${(enviadasPorGrupo.CTVIG||[]).length})` },
    { value:"CTSEG", label:`📤 Seguimiento de CT (${(enviadasPorGrupo.CTSEG||[]).length})` },
    { value:"OCP",   label:`💸 Descuento CxP (${(enviadasPorGrupo.OCP||[]).length})` },
    { value:"RESDIAN", label:`🧾 Resolución DIAN (${(enviadasPorGrupo.RESDIAN||[]).length})` },
    { value:"MATRICULA", label:`🏛️ Renovación de matrícula (${(enviadasPorGrupo.MATRICULA||[]).length})` }
  ];

  const alertasEnviadasHTML = `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:18px;">
      <div style="margin-bottom:12px;">
        <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Filtrar por tipo de documento</label>
        <select style="${inputStyle}min-width:220px;" onchange="state.alertasEnviadasFiltroTipo=this.value;render();">
          ${filtroOpcionesEnviadas.map(o => `<option value="${o.value}" ${filtroEnv===o.value?"selected":""}>${o.label}</option>`).join("")}
        </select>
      </div>
      ${enviadas.length === 0 ? `<div style="font-size:11.5px;color:${C.muted};">Aún no se han procesado ni enviado alertas.</div>` : Object.entries(ALERTA_GRUPO_LABELS).map(([grupo, info]) => {
        if (filtroEnv !== "todos" && filtroEnv !== grupo) return "";
        const items = enviadasPorGrupo[grupo] || [];
        if (items.length === 0) return "";
        return `
          <div style="margin-bottom:6px;">
            <div style="font-size:10.5px;font-weight:700;color:${info.color};margin:10px 0 4px;">${info.titulo} (${items.length})</div>
            ${enviadasOrdenadas(items).map(e => `
              <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 10px;border-top:1px solid ${C.border};gap:10px;flex-wrap:wrap;">
                <div style="font-size:11.5px;color:${C.text};">${escapeHTML(e.linea)} <span style="color:${C.muted};font-size:10.5px;"> · enviada el ${fmtDate(e.fecha)}</span></div>
                <button data-action="reactivar-alerta" data-id="${e.id}" style="${btnStyle(null,"secondary",true)}">↩️ Reactivar</button>
              </div>
            `).join("")}
          </div>
        `;
      }).join("")}
    </div>
  `;

  const alertasTab = state.alertasTab === "enviadas" ? "enviadas" : "pendientes";
  const tabsHTML = `
    <div style="display:flex;gap:6px;margin-bottom:18px;border-bottom:1px solid ${C.border};">
      <button data-action="alertas-tab" data-tab="pendientes" style="
        padding:10px 18px;background:${alertasTab==="pendientes"?"white":"transparent"};
        border:none;border-bottom:3px solid ${alertasTab==="pendientes"?C.accent:"transparent"};
        color:${alertasTab==="pendientes"?"#ef4444":C.muted};font-weight:700;font-size:12px;cursor:pointer;margin-bottom:-1px;font-family:inherit;
      ">🔔 Pendientes (${al.total})</button>
      <button data-action="alertas-tab" data-tab="enviadas" style="
        padding:10px 18px;background:${alertasTab==="enviadas"?"white":"transparent"};
        border:none;border-bottom:3px solid ${alertasTab==="enviadas"?C.accent:"transparent"};
        color:${alertasTab==="enviadas"?"#ef4444":C.muted};font-weight:700;font-size:12px;cursor:pointer;margin-bottom:-1px;font-family:inherit;
      ">✅ Enviadas (${enviadas.length})</button>
    </div>
  `;

  const tabBody = alertasTab === "enviadas"
    ? alertasEnviadasHTML
    : `${filtroBarHTML}${opsHTML}${ctVigHTML}${ctSegHTML}${cxpHTML}${resDianHTML}${matriculaHTML}`;

  return `
    ${header}
    ${configHTML}
    ${tabsHTML}
    ${tabBody}
  `;
}

import { registerActions } from "../ui/actions.js";

registerActions({
  "ir-alertas": (d, el, e) => {{
      navigateTo("alertas");
      return;
    }},
  "ir-configuracion-empresa": (d, el, e) => {{
      navigateTo("config");
      return;
    }},
  "toggle-alerta-check": (d, el, e) => {{
      if (!state.alertasSeleccionadas) state.alertasSeleccionadas = {};
      const key = d.key;
      if (state.alertasSeleccionadas[key]) {
        delete state.alertasSeleccionadas[key];
      } else {
        state.alertasSeleccionadas[key] = true;
      }
      render();
      return;
    }},
  "select-all-alertas": (d, el, e) => {{
      const al = getAlertas();
      const filtro = state.alertasFiltroTipo || "todos";
      const keys = getAlertasKeysByFiltro(al, filtro);
      if (!state.alertasSeleccionadas) state.alertasSeleccionadas = {};
      keys.forEach(k => { state.alertasSeleccionadas[k] = true; });
      render();
      return;
    }},
  "deselect-all-alertas": (d, el, e) => {{
      state.alertasSeleccionadas = {};
      render();
      return;
    }},
  "alertas-tab": (d, el, e) => {{
      state.alertasTab = d.tab;
      render();
      return;
    }},
  "toggle-numero-envio": (d, el, e) => {{
      if (!state.alertConfig.numerosEnvioIds) state.alertConfig.numerosEnvioIds = [];
      const ids = state.alertConfig.numerosEnvioIds;
      const idx = ids.indexOf(d.id);
      if (idx >= 0) ids.splice(idx, 1);
      else ids.push(d.id);
      saveAlertConfig();
      render();
      return;
    }},
  "agregar-whatsapp-numero": (d, el, e) => {{
      const nombreInput = document.getElementById("nuevo-whatsapp-nombre");
      const numeroInput = document.getElementById("nuevo-whatsapp-numero");
      const numero = (numeroInput ? numeroInput.value : "").replace(/[^0-9]/g, "");
      if (!numero) { alert("⚠️ Ingresa un número de WhatsApp válido (solo dígitos, sin código de país)."); return; }
      const nombre = (nombreInput ? nombreInput.value : "").trim();
      if (!state.alertConfig.whatsappNumbers) state.alertConfig.whatsappNumbers = [];
      const nuevo = { id: genId(), nombre: nombre || "Sin nombre", numero };
      state.alertConfig.whatsappNumbers.push(nuevo);
      if (!state.alertConfig.numerosEnvioIds) state.alertConfig.numerosEnvioIds = [];
      if (state.alertConfig.numerosEnvioIds.length === 0) state.alertConfig.numerosEnvioIds.push(nuevo.id);
      saveAlertConfig();
      render();
      return;
    }},
  "eliminar-whatsapp-numero": (d, el, e) => {{
      const id = d.id;
      state.alertConfig.whatsappNumbers = (state.alertConfig.whatsappNumbers || []).filter(n => n.id !== id);
      state.alertConfig.numerosEnvioIds = (state.alertConfig.numerosEnvioIds || []).filter(nid => nid !== id);
      if (state.alertConfig.numeroEnvioId === id) state.alertConfig.numeroEnvioId = null;
      saveAlertConfig();
      render();
      return;
    }},
  "enviar-alertas-seleccionadas": (d, el, e) => {{
      const al = getAlertas();
      const sel = state.alertasSeleccionadas || {};
      const entradas = getAlertasEntradas(al).filter(e => sel[e.key]);
      if (entradas.length === 0) return;
      const numeros = getAlertasNumerosEnvio();
      if (numeros.length === 0) {
        alert("⚠️ Configura y marca al menos un número de WhatsApp en \"Enviar a\" para enviar las alertas.");
        return;
      }

      // Abrir WhatsApp con el mensaje unificado (uno por destinatario marcado)
      const mensaje = buildAlertasMensajeUnificado(al);
      numeros.forEach(numero => window.open(buildAlertasWhatsAppLink(mensaje, numero), "_blank"));

      // Archivar las alertas enviadas y quitarlas del listado activo
      const fecha = today();
      const nuevasEnviadas = entradas.map(e => ({
        id: genId(), key: e.key, grupo: e.grupo,
        docTipo: e.docTipo, docId: e.docId, docNumero: e.docNumero,
        tercero: e.tercero, linea: e.linea, fecha
      }));
      state.data.alertas_enviadas = [...(state.data.alertas_enviadas||[]), ...nuevasEnviadas];
      saveData("alertas_enviadas", state.data.alertas_enviadas);

      // Limpiar selección de las alertas ya procesadas
      entradas.forEach(e => { delete state.alertasSeleccionadas[e.key]; });
      render();
      return;
    }},
  "reactivar-alerta": (d, el, e) => {{
      state.data.alertas_enviadas = (state.data.alertas_enviadas||[]).filter(e => e.id !== d.id);
      saveData("alertas_enviadas", state.data.alertas_enviadas);
      render();
      return;
    }},
});
